import React, { useContext, ReactElement, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";

interface PlatformProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * 🔒 PLATFORM PROTECTED ROUTE
 * High-security guard for Super Admin infrastructure access.
 * Validates:
 * 1. Authentication state
 * 2. SUPER_ADMIN role
 * 3. Platform-scope (tenantId === null)
 * 4. Anti-Impersonation (prevents nested access while impersonating)
 */
const PlatformProtectedRoute: React.FC<PlatformProtectedRouteProps> = ({ children }): ReactElement | null => {
  const { isPlatformAuthenticated, platformUser } = useContext(AuthContext);
  const navigate = useNavigate();

  // 🛡️ SECURITY: Strict role and scope validation for Platform session
  const isGlobalAdmin = platformUser?.role === "SUPER_ADMIN";

  useEffect(() => {
    // If not authenticated or not a Super Admin, redirect to platform login
    if (!isPlatformAuthenticated || !isGlobalAdmin) {
      navigate("/platform/login", { replace: true });
    }
  }, [isPlatformAuthenticated, isGlobalAdmin, navigate]);

  if (!isPlatformAuthenticated || !isGlobalAdmin) {
    return null; // Prevent flicker while redirecting
  }

  if (!isGlobalAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center bg-[#0f172a] p-8">
        <div className="max-w-md space-y-4">
          <div className="text-red-500 font-semibold text-6xl mb-4">403</div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Access Restricted</h1>
          <p className="text-slate-400 text-sm">
            This section is restricted to Infrastructure Administrators. 
            Your account does not have the required permissions.
          </p>
          <button 
            onClick={() => window.location.href = "/login"}
            className="btn-primary"
          >
            Return to Safety
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default PlatformProtectedRoute;
